import { Component, effect, inject, input, model, output, signal } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { HttpErrorResponse } from "@angular/common/http";
import { firstValueFrom } from "rxjs";

import { NzButtonModule } from "ng-zorro-antd/button";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { NzSelectModule } from "ng-zorro-antd/select";

import { ProjectService } from "../../../shared/services/project.service";
import { AWS_REGIONS } from "../../../shared/aws-regions";
import { httpErrorMessage } from "../../../shared/http-error-message";
import { S3BucketInterface } from "../../../models/s3-bucket.model";

/** S3 naming rules: 3-63 chars, lowercase alphanumerics plus dot/hyphen, no leading or trailing separator. */
const BUCKET_NAME_PATTERN = /^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/;

@Component({
  selector: "app-bucket-form",
  imports: [
    ReactiveFormsModule,
    NzButtonModule,
    NzFormModule,
    NzIconModule,
    NzInputModule,
    NzSelectModule,
  ],
  templateUrl: "./bucket-form.component.html",
  styleUrl: "./bucket-form.component.less",
})
export class BucketFormComponent {
  private projectService = inject(ProjectService);
  private notification = inject(NzNotificationService);
  private fb = inject(FormBuilder);

  /** Emitted after a bucket is registered or updated, so the owner can refresh its list. */
  readonly saved = output<void>();

  protected isSubmitting = signal(false);
  protected readonly regions = AWS_REGIONS;
  protected readonly hideButtons = input<boolean>(false)
  protected readonly activeBucket = model<S3BucketInterface | null>(null)

  protected bucketForm = this.fb.nonNullable.group({
    name: ["", [Validators.required, Validators.pattern(BUCKET_NAME_PATTERN)]],
    alias: ["", [Validators.maxLength(64)]],
    region: ["us-east-1", [Validators.required]],
    description: ["", [Validators.maxLength(255)]],
  });

  constructor(){
    // if editting a bucket, this goes through
    effect(() => {
      const bucket = this.activeBucket()
      if (bucket){
        this.bucketForm.setValue({
          name: bucket.name,
          alias: bucket?.alias,
          region: bucket.region,
          description: bucket?.description
        })
      }
    })
  }

  protected cancelEdit() {
    this.bucketForm.enable();
    this.bucketForm.reset(); // nonNullable → region back to us-east-1
    this.activeBucket?.set(null)
  }

  public async submitForm() {
    if (this.isSubmitting()) return;
    if (this.bucketForm.invalid) {
      this.bucketForm.markAllAsTouched()
      Object.values(this.bucketForm.controls).forEach((control) => {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      });
      return
    };

    const id = this.activeBucket()?.id;
    const bucket = this.bucketForm.getRawValue(); // includes the disabled name, unchanged

    this.isSubmitting.set(true);
    try {
      await firstValueFrom(
        id
          ? this.projectService.editS3Bucket(id, bucket)
          : this.projectService.registerS3Bucket(bucket),
      );
      this.notification.success(
        "Success",
        id ? "Bucket updated." : "Bucket registered.",
      );
      this.saved.emit();
      this.cancelEdit();
    } catch (err) {
      this.notification.error("Error", httpErrorMessage(err as HttpErrorResponse));
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
